import { Flex, Icon, IconButton, Text, useToast } from "@chakra-ui/react";
import { t } from "i18next";
import { cloneDeep, isEqual } from "lodash";
import { useCallback } from "react";
import { FaTrash } from "react-icons/fa";
import {
  DefaultNotificationSettingsDto,
  FrameworkAgreementRole,
  FrameworkValueReminderDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../common/subscription/useSub";
import { useSaveOrganization } from "../../organizations/current/useOrganizationEditor";
import { frameworkValueReminderFailure } from "./frameworkValueReminderFailure";
import { frameworkValueReminders, initSettings } from "./NotificationSettings";
import { useReceiverRoleNames } from "./useReceiverRoleNames";

export const FrameworkValueReminderSetting = ({
  setting,
}: {
  setting: FrameworkValueReminderDto;
}) => {
  const { canUpdateOrganization } = useSub();
  const authState = useLoggedInWithOrgContextState();
  const toast = useToast();

  const { saveOrganization, isSaving } = useSaveOrganization();

  const deleteSetting = useCallback(async () => {
    const settings: DefaultNotificationSettingsDto =
      cloneDeep(authState.selectedOrg.defaultNotificationSettings) ??
      initSettings();
    settings.frameworkAgreementNotificationSettings = {
      valueNotifications: frameworkValueReminders(settings).filter(
        (s) => !isEqual(s, setting),
      ),
    };
    try {
      await saveOrganization({ defaultNotificationSettings: settings });
    } catch (e) {
      toast(
        frameworkValueReminderFailure(
          t("The reminder was not deleted"),
          e,
        ),
      );
    }
  }, [
    authState.selectedOrg.defaultNotificationSettings,
    setting,
    toast,
    saveOrganization,
  ]);

  return (
    <FrameworkValueReminderCard
      setting={setting}
      onDelete={canUpdateOrganization ? deleteSetting : undefined}
      isDeleting={isSaving}
    />
  );
};

export const FrameworkValueReminderCard = ({
  setting,
  onDelete,
  isDeleting = false,
}: {
  setting: FrameworkValueReminderDto;
  onDelete?: () => void;
  isDeleting?: boolean;
}) => {
  const roleName = useReceiverRoleNames();
  return (
    <Flex
      flexDirection={"column"}
      fontSize={"sm"}
      backgroundColor="smBackgroundSecondary"
      rounded="md"
      p="2"
      my="1"
    >
      <Flex justifyContent={"space-between"} width="full">
        <Text>{t("Time of notification")}</Text>
        {onDelete && (
          <IconButton
            aria-label={t("Delete setting")}
            icon={<Icon as={FaTrash} />}
            size={"xs"}
            isLoading={isDeleting}
            onClick={() => onDelete()}
          />
        )}
      </Flex>
      <Text fontWeight={"bold"}>
        {t("When the call-offs reach {{percentage}} % of the ceiling value", {
          percentage: setting.percentage,
        })}
      </Text>
      <Text>{t("Individual receivers")}</Text>
      {setting.receivers.persons.map((person) => (
        <Text key={person.id} fontWeight={"bold"}>
          {person.firstName} {person.lastName}
        </Text>
      ))}
      <Text>{t("Organization role receivers")}</Text>
      {setting.receivers.organizationRoles.map((role) => (
        <Text key={role} fontWeight={"bold"}>
          {roleName.organizationRole(role)}
        </Text>
      ))}
      <Text>{t("Framework agreement role receivers")}</Text>
      {setting.receivers.frameworkAgreementRoles.includes(
        FrameworkAgreementRole.Responsible,
      ) && (
        <Text fontWeight={"bold"}>
          {t("Responsible for the framework agreement")}
        </Text>
      )}
    </Flex>
  );
};
